import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Check, X, Crown, Trophy, RefreshCw, Eye, Instagram, Twitter, Youtube, Globe,
  MessageCircle, Music, Sparkles,
} from "lucide-react";

const TIER_LABEL = {
  advocate: "Advocate", ambassador: "Ambassador",
  elite: "Elite", regional: "Regional Director",
};

export default function AmbassadorAdmin() {
  const [section, setSection] = useState("applications");
  return (
    <div className="space-y-6" data-testid="admin-ambassador">
      <div className="flex items-center gap-2 border-b border-secondary/15">
        {[
          ["applications", "Applications"],
          ["roster",       "Active Roster"],
          ["leaderboard",  "Leaderboard"],
          ["analytics",    "Analytics"],
          ["notifications","Admin Alerts"],
          ["settings",     "Program Settings"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setSection(k)}
            className={`px-4 py-2.5 text-xs uppercase tracking-[0.2em] ${
              section === k
                ? "text-secondary border-b-2 border-secondary -mb-px"
                : "text-foreground/55 hover:text-foreground/85"
            }`}
            data-testid={`amb-admin-tab-${k}`}>
            {l}
          </button>
        ))}
      </div>
      {section === "applications" && <ApplicationsQueue />}
      {section === "roster" && <Roster />}
      {section === "leaderboard" && <Leaderboard />}
      {section === "analytics" && <AnalyticsPanel />}
      {section === "notifications" && <AdminAlertsPanel />}
      {section === "settings" && <ProgramSettings />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Analytics — growth pulse for the launch window
// ─────────────────────────────────────────────────────────────────────────
function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/ambassadors/analytics?days=${days}`);
      setData(data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading || !data) {
    return <div className="text-foreground/55 text-sm py-12 text-center">Loading…</div>;
  }

  // Build a dense date-aligned bar chart for daily signups
  const bucket = {};
  data.dailySignups.forEach((s) => { bucket[s.day] = s.count; });
  const now = new Date();
  const chart = [];
  for (let i = data.days - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    chart.push({ day: key, count: bucket[key] || 0 });
  }
  const maxCount = Math.max(1, ...chart.map((c) => c.count));

  return (
    <div className="space-y-8" data-testid="amb-analytics-panel">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="label-mystic text-secondary">Ambassador Analytics</div>
          <p className="text-foreground/60 text-sm mt-1">
            Growth pulse for the last {data.days} days. All figures live from the ledger.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-input border border-secondary/25 rounded-sm px-3 py-2 text-sm"
          data-testid="analytics-days-select"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Active Ambassadors" value={data.totals.activeAmbassadors} testid="stat-active" />
        <StatTile label="Founding-20 Claimed" value={`${data.totals.founding20Claimed}/${data.totals.founding20Cap}`} testid="stat-founding" />
        <StatTile label="CAUL Bonuses Paid" value={data.caulBonuses.totalCaul.toLocaleString()} sub={`${data.caulBonuses.bonusesPaid} bonuses`} testid="stat-caul" />
        <StatTile label="Pending Digest" value={data.pendingAdminDigest} sub="signups queued" testid="stat-pending" />
      </div>

      {/* Daily signup chart */}
      <div className="parchment p-5">
        <div className="label-mystic text-secondary mb-4">Daily Signups</div>
        <div className="flex items-end gap-1 h-32">
          {chart.map((c) => (
            <div key={c.day} className="flex-1 flex flex-col items-center gap-1 group relative" title={`${c.day}: ${c.count}`}>
              <div
                className={`w-full rounded-sm transition-all ${c.count > 0 ? "bg-secondary" : "bg-foreground/10"}`}
                style={{ height: `${(c.count / maxCount) * 100}%`, minHeight: c.count > 0 ? "4px" : "2px" }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-foreground/50 mt-2">
          <span>{chart[0]?.day}</span>
          <span>{chart[chart.length - 1]?.day}</span>
        </div>
      </div>

      {/* Top referrers */}
      <div className="parchment p-5">
        <div className="label-mystic text-secondary mb-4">Top Referrers</div>
        {data.topReferrers.length === 0 ? (
          <div className="text-sm text-foreground/55">No paid practitioner referrals yet — will populate as ambassadors close their first deals.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-foreground/50 uppercase tracking-wider">
              <tr><th className="text-left pb-2">Name</th><th className="text-left pb-2">Tier</th><th className="text-right pb-2">Refs</th><th className="text-right pb-2">USD</th><th className="text-right pb-2">CAUL</th></tr>
            </thead>
            <tbody>
              {data.topReferrers.map((r) => (
                <tr key={r.userId} className="border-t border-foreground/5" data-testid={`referrer-${r.userId}`}>
                  <td className="py-2">{r.name}<span className="text-[11px] text-foreground/50 ml-1.5">({r.referralCode})</span></td>
                  <td className="py-2 capitalize text-foreground/70">{r.tier}</td>
                  <td className="py-2 text-right text-secondary">{r.referrals}</td>
                  <td className="py-2 text-right">${r.commissionUsd.toFixed(2)}</td>
                  <td className="py-2 text-right">{r.commissionCaul}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Landing page attribution */}
      <div className="parchment p-5">
        <div className="label-mystic text-secondary mb-4">Landing-Page Traffic</div>
        {data.landingPages.length === 0 ? (
          <div className="text-sm text-foreground/55">
            No landing-page traffic yet. Once ambassadors share their niche URLs
            (like <code className="text-secondary">/for/reiki-masters?ref=CODE</code>), hits appear here.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-foreground/50 uppercase tracking-wider">
              <tr><th className="text-left pb-2">Path</th><th className="text-right pb-2">Hits</th><th className="text-right pb-2">Unique Ambassadors</th></tr>
            </thead>
            <tbody>
              {data.landingPages.map((p) => (
                <tr key={p.path} className="border-t border-foreground/5">
                  <td className="py-2 font-mono text-[12px]">{p.path}</td>
                  <td className="py-2 text-right text-secondary">{p.hits}</td>
                  <td className="py-2 text-right">{p.uniqueAmbassadorLinks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, testid }) {
  return (
    <div className="parchment p-4" data-testid={testid}>
      <div className="label-mystic text-foreground/55 text-[10px]">{label}</div>
      <div className="font-serif text-3xl mt-2 text-secondary">{value}</div>
      {sub && <div className="text-[11px] text-foreground/50 mt-1">{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Admin Alerts — realtime vs digest email mode
// ─────────────────────────────────────────────────────────────────────────
function AdminAlertsPanel() {
  const [mode, setMode] = useState(null);
  const [pending, setPending] = useState(0);
  const [saving, setSaving] = useState(false);
  const [flushing, setFlushing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ambassadors/digest-mode");
      setMode(data.mode);
      setPending(data.pendingCount);
    } catch {
      toast.error("Failed to load digest settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (newMode) => {
    setSaving(true);
    try {
      await api.post("/admin/ambassadors/digest-mode", { mode: newMode });
      setMode(newMode);
      toast.success(`Switched to ${newMode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const flush = async () => {
    setFlushing(true);
    try {
      const { data } = await api.post("/admin/ambassadors/flush-digest");
      toast.success(`Sent digest with ${data.flushed} signup${data.flushed === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Flush failed");
    } finally {
      setFlushing(false);
    }
  };

  if (mode === null) return <div className="text-foreground/55 text-sm py-12 text-center">Loading…</div>;

  const options = [
    { id: "realtime", label: "Real-time", desc: "Email admins on every ambassador signup. Best for launch & low-volume phases." },
    { id: "daily",    label: "Daily digest", desc: "Queue signups. Send one summary email each day at 08:00 UTC." },
    { id: "weekly",   label: "Weekly digest", desc: "Queue signups. Send one summary each Monday at 08:00 UTC." },
  ];

  return (
    <div className="space-y-6" data-testid="amb-notifications-panel">
      <div>
        <div className="label-mystic text-secondary">Signup Admin Alerts</div>
        <p className="text-foreground/60 text-sm mt-1">
          Choose how you receive admin notifications when a new ambassador signs up.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {options.map((o) => {
          const active = mode === o.id;
          return (
            <button
              key={o.id}
              onClick={() => !active && save(o.id)}
              disabled={saving || active}
              className={`text-left p-4 rounded-sm border transition ${
                active
                  ? "border-secondary bg-secondary/10"
                  : "border-secondary/25 hover:border-secondary/60 hover:bg-secondary/5"
              }`}
              data-testid={`digest-mode-${o.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-serif text-lg">{o.label}</div>
                {active && <span className="text-[10px] uppercase tracking-wider text-secondary">Active</span>}
              </div>
              <div className="text-[11px] text-foreground/60 mt-1 leading-relaxed">{o.desc}</div>
            </button>
          );
        })}
      </div>

      {mode !== "realtime" && (
        <div className="parchment p-5 flex items-center justify-between gap-3" data-testid="digest-flush-panel">
          <div>
            <div className="text-sm">
              <strong className="text-secondary" data-testid="digest-pending-count">{pending}</strong> signup{pending === 1 ? "" : "s"} queued for the next digest.
            </div>
            <div className="text-[11px] text-foreground/55 mt-1">
              Next auto-flush at 08:00 UTC{mode === "weekly" ? " each Monday" : ""}. Or send now:
            </div>
          </div>
          <button
            onClick={flush}
            disabled={flushing || pending === 0}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/85 disabled:opacity-50 px-5 py-2.5 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold"
            data-testid="digest-flush-btn"
          >
            {flushing ? "Sending…" : "Send Now"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Applications Queue
// ─────────────────────────────────────────────────────────────────────────
function ApplicationsQueue() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/ambassadors/applications?status=${status}`);
        if (!cancelled) setRows(data.rows || []);
      } catch {
        if (!cancelled) toast.error("Failed to load applications");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status, reloadKey]);

  const decide = async (aid, approve, payload) => {
    try {
      await api.post(
        `/admin/ambassadors/applications/${aid}/${approve ? "approve" : "reject"}`,
        payload,
      );
      toast.success(approve ? "Approved" : "Rejected");
      setExpanded(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="amb-applications">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm transition ${
                status === s
                  ? "bg-secondary text-secondary-foreground"
                  : "border border-foreground/15 text-foreground/65 hover:border-secondary/45"
              }`}
              data-testid={`amb-status-${s}`}>{s}</button>
          ))}
        </div>
        <button onClick={() => setReloadKey((k) => k + 1)}
          className="text-foreground/65 hover:text-secondary p-2 rounded-sm border border-foreground/10"
          data-testid="amb-reload">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {loading && <div className="text-foreground/55 text-sm py-12 text-center">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="parchment p-12 text-center text-foreground/65">
          No <strong>{status}</strong> applications.
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((a) => (
            <ApplicationCard
              key={a.id} app={a}
              isOpen={expanded === a.id}
              onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
              onDecide={decide}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationCard({ app, isOpen, onToggle, onDecide }) {
  const [tier, setTier] = useState("advocate");
  const [makeFounding, setMakeFounding] = useState(false);
  const [note, setNote] = useState("");

  const social = app.socials || {};
  const socialIcons = [
    ["instagram", Instagram, social.instagram],
    ["tiktok",    Music,     social.tiktok],
    ["x",         Twitter,   social.x],
    ["youtube",   Youtube,   social.youtube],
    ["website",   Globe,     social.website],
    ["discord",   MessageCircle, social.discord],
  ].filter(([, , v]) => !!v);

  return (
    <div className="parchment p-4" data-testid={`amb-app-${app.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-serif text-lg">{app.name}</div>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-foreground/15 text-foreground/65">
              {app.status}
            </span>
            {app.tradition && (
              <span className="text-[10px] uppercase tracking-wider text-secondary">
                {app.tradition}
              </span>
            )}
          </div>
          <div className="text-xs text-foreground/55 mt-0.5">{app.email}</div>
          <div className="text-xs text-foreground/65 mt-2 line-clamp-1">{app.audience}</div>
        </div>
        <div className="flex items-center gap-2">
          {socialIcons.slice(0, 3).map(([k, Icon]) => (
            <Icon key={k} className="w-3.5 h-3.5 text-secondary/85" />
          ))}
          <button onClick={onToggle}
            className="border border-foreground/15 text-foreground/75 hover:border-secondary/45 px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm"
            data-testid={`amb-app-toggle-${app.id}`}>
            <Eye className="w-3.5 h-3.5 inline mr-1" /> Review
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="mt-5 pt-5 border-t border-foreground/10 space-y-4 text-sm" data-testid={`amb-app-detail-${app.id}`}>
          <Detail label="Bio">{app.bio}</Detail>
          {app.whyJoin && <Detail label="Why Cauldron?">{app.whyJoin}</Detail>}
          <Detail label="Audience">{app.audience}{app.audienceSize ? ` · ~${app.audienceSize.toLocaleString()} people` : ""}</Detail>
          {socialIcons.length > 0 && (
            <Detail label="Socials">
              <div className="flex flex-wrap gap-2 mt-1">
                {socialIcons.map(([k, Icon, v]) => (
                  <span key={k} className="inline-flex items-center gap-1.5 text-xs text-foreground/75 border border-foreground/10 px-2 py-1 rounded-sm">
                    <Icon className="w-3 h-3 text-secondary" /> {v}
                  </span>
                ))}
              </div>
            </Detail>
          )}
          {app.referredBy && (
            <Detail label="Referred by">
              <span className="font-mono text-xs">{app.refCode}</span>
            </Detail>
          )}
          {app.status === "pending" && (
            <div className="mt-6 pt-5 border-t border-foreground/10 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="label-mystic text-foreground/65 mb-2">Approve as tier</div>
                  <select value={tier} onChange={(e) => setTier(e.target.value)}
                    className="w-full bg-input border border-secondary/20 p-2.5 rounded-sm text-sm"
                    data-testid={`amb-app-tier-${app.id}`}>
                    {Object.entries(TIER_LABEL).map(([k, l]) => (
                      <option key={k} value={k}>{l}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 self-end pb-1 cursor-pointer">
                  <input type="checkbox" checked={makeFounding}
                    onChange={(e) => setMakeFounding(e.target.checked)}
                    className="accent-secondary"
                    data-testid={`amb-app-founding-${app.id}`} />
                  <span className="text-sm text-foreground/85 flex items-center gap-1">
                    <Crown className="w-3.5 h-3.5 text-secondary" /> Grant Founding Status
                  </span>
                </label>
              </div>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Internal note (optional)"
                className="w-full bg-input border border-secondary/20 p-2.5 rounded-sm text-sm"
                data-testid={`amb-app-note-${app.id}`} />
              <div className="flex gap-2 justify-end">
                <button onClick={() => onDecide(app.id, false, { note, tier })}
                  className="border border-destructive/40 text-destructive hover:bg-destructive/10 px-4 py-2 text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-1.5"
                  data-testid={`amb-app-reject-${app.id}`}>
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
                <button onClick={() => onDecide(app.id, true, { note, tier, makeFounding })}
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/85 px-4 py-2 text-xs uppercase tracking-wider rounded-sm inline-flex items-center gap-1.5"
                  data-testid={`amb-app-approve-${app.id}`}>
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
              </div>
            </div>
          )}
          {app.note && app.status !== "pending" && (
            <Detail label="Decision note">{app.note}</Detail>
          )}
        </div>
      )}
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="label-mystic text-foreground/55 mb-1">{label}</div>
      <div className="text-foreground/85 leading-relaxed">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Roster
// ─────────────────────────────────────────────────────────────────────────
function Roster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/ambassadors");
        if (!cancelled) setRows(data.rows);
      } catch {
        if (!cancelled) toast.error("Failed to load roster");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const setTier = async (uid, tier) => {
    try {
      await api.post(`/admin/ambassadors/${uid}/tier`, { tier });
      toast.success(`Set to ${TIER_LABEL[tier]}`);
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-foreground/55 text-sm py-12 text-center">Loading…</div>;

  return (
    <div className="parchment p-2" data-testid="amb-roster">
      {rows.length === 0 ? (
        <div className="p-12 text-center text-foreground/65">No active ambassadors yet.</div>
      ) : (
        <div className="divide-y divide-foreground/5">
          {rows.map((a) => (
            <div key={a.userId} className="flex items-center gap-3 p-3 hover:bg-secondary/[0.03]" data-testid={`amb-roster-row-${a.userId}`}>
              {a.imageUrl ? (
                <img src={a.imageUrl} alt={a.name} className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-secondary/15 flex items-center justify-center text-secondary font-serif">{a.name?.[0] || "?"}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-serif flex items-center gap-1.5">
                  {a.name}
                  {a.isFounding && <Crown className="w-3 h-3 text-secondary" title={`Founding #${a.foundingNumber}`} />}
                </div>
                <div className="text-[11px] text-foreground/55 truncate">{a.email}</div>
              </div>
              <select value={a.tier} onChange={(e) => setTier(a.userId, e.target.value)}
                className="bg-input border border-secondary/20 px-2 py-1 rounded-sm text-xs"
                data-testid={`amb-roster-tier-${a.userId}`}>
                {Object.entries(TIER_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <div className="text-right w-24">
                <div className="font-serif text-sm text-secondary">${a.totalCommissionUsd?.toFixed(0) || 0}</div>
                <div className="text-[10px] text-foreground/55">{a.totalPractitionerReferrals || 0} subs</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Leaderboard view (re-uses public endpoint with admin context)
// ─────────────────────────────────────────────────────────────────────────
function Leaderboard() {
  const [period, setPeriod] = useState("30d");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/ambassador/leaderboard?period=${period}&limit=50`);
        if (!cancelled) setRows(data.rows);
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, [period]);
  return (
    <div className="space-y-4" data-testid="amb-admin-leaderboard">
      <div className="flex gap-2">
        {[["30d", "Last 30d"], ["all", "All-time"]].map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm ${
              period === k
                ? "bg-secondary text-secondary-foreground"
                : "border border-foreground/15 text-foreground/65"
            }`}
            data-testid={`amb-board-period-${k}`}>{l}</button>
        ))}
      </div>
      <div className="parchment p-2">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-foreground/55 text-sm">No commission activity in this window yet.</div>
        ) : (
          <div className="divide-y divide-foreground/5">
            {rows.map((r) => (
              <div key={r.userId} className="flex items-center gap-3 p-3">
                <div className={`font-serif text-lg w-10 ${r.rank <= 3 ? "text-secondary" : "text-foreground/55"}`}>#{r.rank}</div>
                <div className="flex-1 truncate">
                  <span className="text-sm">{r.name}</span>
                  {r.isFounding && <Crown className="inline w-3 h-3 text-secondary ml-1" />}
                </div>
                <div className="text-xs text-foreground/55 mr-3">{r.subs} subs</div>
                <div className="font-serif text-sm text-secondary w-20 text-right">${r.usd.toFixed(0)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Program Settings
// ─────────────────────────────────────────────────────────────────────────
function ProgramSettings() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ambassador/program-config");
        if (!cancelled) setS(data);
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    try {
      await api.post("/admin/ambassador-settings", {
        commissionUsdCentsByTier: s.commissionUsdCentsByTier,
        tierThresholds: s.tierThresholds,
        foundingMaxSlots: parseInt(s.foundingMaxSlots),
        publicApplyOpen: s.publicApplyOpen,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!s) return <div className="text-foreground/55 text-sm py-12 text-center">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="amb-settings">
      <div className="parchment p-6">
        <div className="label-mystic text-secondary mb-4">Commission Rates (cents/mo per sub)</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(s.commissionUsdCentsByTier).map(([k, v]) => (
            <div key={k}>
              <div className="text-[11px] uppercase tracking-wider text-foreground/60 mb-1">{TIER_LABEL[k]}</div>
              <input type="number" value={v}
                onChange={(e) => setS({ ...s, commissionUsdCentsByTier: { ...s.commissionUsdCentsByTier, [k]: parseInt(e.target.value || "0") } })}
                className="w-full bg-input border border-secondary/20 p-2.5 rounded-sm font-mono text-sm"
                data-testid={`amb-rate-${k}`} />
              <div className="text-[10px] text-foreground/55 mt-1">${(v / 100).toFixed(2)}/mo</div>
            </div>
          ))}
        </div>
      </div>
      <div className="parchment p-6">
        <div className="label-mystic text-secondary mb-4">Tier Thresholds (active referrals)</div>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(s.tierThresholds).map(([k, v]) => (
            <div key={k}>
              <div className="text-[11px] uppercase tracking-wider text-foreground/60 mb-1">{TIER_LABEL[k]}</div>
              <input type="number" value={v}
                onChange={(e) => setS({ ...s, tierThresholds: { ...s.tierThresholds, [k]: parseInt(e.target.value || "0") } })}
                className="w-full bg-input border border-secondary/20 p-2.5 rounded-sm font-mono text-sm"
                data-testid={`amb-thresh-${k}`} />
            </div>
          ))}
        </div>
      </div>
      <div className="parchment p-6">
        <div className="label-mystic text-secondary mb-4">Founding Campaign</div>
        <div className="grid grid-cols-2 gap-4 items-end">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-foreground/60 mb-1">Max Founding Slots</div>
            <input type="number" value={s.foundingMaxSlots}
              onChange={(e) => setS({ ...s, foundingMaxSlots: parseInt(e.target.value || "0") })}
              className="w-full bg-input border border-secondary/20 p-2.5 rounded-sm font-mono text-sm"
              data-testid="amb-founding-cap" />
          </div>
          <label className="flex items-center gap-2 pb-2">
            <input type="checkbox" checked={s.publicApplyOpen}
              onChange={(e) => setS({ ...s, publicApplyOpen: e.target.checked })}
              className="accent-secondary" data-testid="amb-public-open" />
            <span className="text-sm">Public applications open</span>
          </label>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={save}
          className="bg-secondary text-secondary-foreground hover:bg-secondary/85 px-6 py-3 rounded-sm uppercase tracking-wider text-xs"
          data-testid="amb-settings-save">
          Save changes
        </button>
      </div>
    </div>
  );
}
